import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/ember';
import type { Store } from '@warp-drive/core';
import type { ObjectValue } from '@warp-drive/core-types/json/raw';
import CompanyModel from 'bcl/models/company';
import UserModel from 'bcl/models/user';

export default class SessionService extends Service {
  @service declare router: RouterService;
  @service declare store: Store;
  @tracked _state: null | SessionState = null;

  @tracked orderKind: string | undefined = undefined; //'frei';
  @tracked orderSuplID: string | null = null; //'1';

  stocktakingSkipInital = false;

  get user_id() {
    return this._state?.uid ?? 0;
  }
  get user() {
    return this.store.peekRecord<UserModel>('user', this.user_id);
  }
  get company_id() {
    return this._state?.cid ?? 0;
  }
  get company() {
    return this.store.peekRecord<CompanyModel>('company', this.company_id);
  }
  get authenticated() {
    return this._state?.uid ?? false;
  }
  get isAdmin() {
    return this._state?.role === 'admin';
  }
  get isSpruce() {
    return this.user?.email?.includes('@spruce.de');
  }
  // angelehnt an Ember simple auth
  @action async prohibitAuthentication() {
    if (!this._state) {
      await this.loadSession();
    }
    if (this.authenticated) {
      this.router.transitionTo('auth');
    }
  }
  @action async authenticate(email: string, password: string) {
    const resp = await fetch('/api/sessions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (resp.status === 404) {
      throw new Error('Falsche Emailadresse oder falsches Passwort.');
    }
    return resp;
  }
  @action async unauthenticate() {
    const resp = await fetch('/api/sessions', {
      method: 'DELETE',
    });
    if (resp.status === 200) {
      this._state = null;
      Sentry.setUser(null);
      this.store.unloadAll();
    }
    return resp;
  }
  @action async requireAuthentication() {
    if (!this._state) {
      await this.loadSession();
    }
    if (!this.authenticated) {
      this.router.transitionTo('login');
    }
  }
  @action async loadSession() {
    try {
      const resp = await fetch('/api/sessions', {
        signal: AbortSignal.timeout(5000),
      });

      if (resp?.status === 200) {
        // falls wir hier eine session bekommen, dann müssen wir das hier irgendwie setzen
        // ansonsten weiterleiten
        const allResp = (await resp.json()) as SessionResponse;
        this._state = allResp.session;
        // eslint-disable-next-line
        this.store.pushPayload(allResp.company);
        // eslint-disable-next-line
        this.store.pushPayload(allResp.users);
        Sentry.setUser({ id: +this.user_id! });
      } else {
        this._state = null;
      }
    } catch (err) {
      const error = err as DOMException;
      if (error?.name === 'TimeoutError') {
        alert(
          'Es hat länger al 5 Sekunden gedauert, bis der Server geantwortet hat. Versuchen Sie es in in einigen Augenblicken noch einmal'
        );
      } else if (error?.name === 'AbortError') {
        // ignore
      }
      console.error(err);
      this.router.transitionTo('login');
    }
  }
}
type SessionResponse = {
  company: ObjectValue;
  users: ObjectValue;
  session: SessionState;
};

type SessionState = {
  uid: number;
  cid: number;
  role: string;
  perms: Array<string>;
};

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:session')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('session') declare altName: SessionService;`.
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
